import i18n from '@/i18n';
import originalAxios from 'axios';
import axios from '@/axios-api';
import Pathes from '@/helpers/pathes';
import config from '@/config';
import { getRequestHeaderParams } from '@/axios-api';
import { getMessage } from '@/helpers/utils';
import Notify from '@/components/Notification';
import { clearDirectAccessToken, clearRefreshSessionHint } from '@/helpers/authSession';
import { removeUserFromCookie } from '@/helpers/sharedUser';
import { queryClient } from '@/queryClient';
import { AppRoutes } from '@/routes';
import { APP_LINKS } from '@/helpers/links';
import { useUserStore } from '@/store/userStore';
import { ChatwootChat } from '@/chat/chatwoot';
import { discardInFlightRefresh } from '@/store/tokenRefresh';

const { t } = i18n;

export const updateProfilePhoto = photo => {
  return axios
    .post(Pathes.User.updateProfilePhoto, { photo })
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({
          text: photo
            ? t('kit:notify.photo_updated')
            : t('kit:notify.photo_removed'),
        });
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const updateProfileSummary = payload => {
  return axios
    .post(Pathes.User.doUpdatePersonalInfo, payload)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({ text: t('kit:notify.profile_details_updated') });
        return { data, success: true };
      }

      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const updateProfileContacts = payload => {
  return axios
    .post(Pathes.User.doUpdateContactInfo, payload)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({ text: t('kit:notify.personal_information_updated') });
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const toggleSubscriptionEmails = (token, current) => {
  return axios
    .post(
      current ? Pathes.User.subscribeEmails : Pathes.User.unsubscribeEmails,
      { token },
    )
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({ text: message });
        return { data, success: true };
      }

      Notify.error({ text: message });
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const setUserVeriffSkipReason = reason => {
  return axios
    .post(Pathes.User.veriffSkip, { reason })
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const setInvestorType = investorType => {
  return axios
    .post(Pathes.User.investorType, investorType)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const getPhoneSentCodes = () => {
  return axios
    .get(Pathes.User.sentCodes)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const validatePhoneNumber = payload => {
  return axios
    .post(Pathes.User.validatePhoneNumber, payload)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({ text: message });
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const sendVerificationCode = payload => {
  return axios
    .post(Pathes.User.sentCodes, payload)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const verifyUserEmail = code => {
  return axios
    .post(Pathes.User.emailVerification, { code })
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, error_code: data.error_code, success: true };
      }
      return { success: false, error_code: data?.error_code, message };
    })
    .catch(e => ({ success: false, error_code: null, message: e.message }));
};

export const submitEmailVerification = () => {
  return axios
    .post(Pathes.User.sendEmailVerificationCode)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        Notify.success({ text: message });
        return { data, success: true };
      }
      message && Notify.error({ text: message });
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const getTermsOfUseStatus = () => {
  return axios
    .get(Pathes.Investing.termsOfUseStatus)
    .then(response => {
      const { status, data } = response;
      const message = getMessage(data);
      if (status === 200) {
        return { data, success: true };
      }
      throw new Error(message);
    })
    .catch(e => ({ success: false, message: e.message }));
};

export const clearLocalSession = ({ redirect = true } = {}) => {
  discardInFlightRefresh();
  ChatwootChat.reset();
  clearRefreshSessionHint();
  clearDirectAccessToken();
  removeUserFromCookie();
  useUserStore.getState().clearTokens();
  queryClient.clear();
  if (redirect) {
    AppRoutes.navigate(APP_LINKS.auth);
  }
};

// Dedupe concurrent callers: several parallel 401s used to fire one
// POST /logout/ each, and the reload masked it.
let logoutInFlight = null;

export const logoutUser = () => {
  if (logoutInFlight) return logoutInFlight;

  discardInFlightRefresh();

  logoutInFlight = originalAxios
    .post(`${config.apiURL}${Pathes.Auth.logout}`, undefined, {
      withCredentials: true,
      headers: getRequestHeaderParams(),
    })
    .catch(() => {
      // logout is best-effort: local state must be cleared either way
    })
    .finally(() => {
      try {
        clearLocalSession();
      } catch (e) {
        console.error(e);
      } finally {
        logoutInFlight = null;
      }
    });

  return logoutInFlight;
};
